import React, { useState, useRef } from "react";
import Modal from "../../../shared/components/ui/Modal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

const UploadGreetingModal = ({ show, number, onCancel, onUpload }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const allowedTypes = [
    "audio/mpeg",
    "audio/mp3",
  ];
  const maxSizeMB = 5;

  const validateFile = (f) => {
    if (!allowedTypes.includes(f.type)) {
      setError("Only .mp3 files are allowed.");
      return false;
    }
    if (f.size > maxSizeMB * 1024 * 1024) {
      setError(`File must be less than ${maxSizeMB}MB.`);
      return false;
    }
    setError("");
    return true;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  const handleSubmit = () => {
    if (file) {
      onUpload(number, file);
      setFile(null);
      setError("");
    }
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    onCancel();
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-primary">
            Upload Voicemail Greeting
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      }
      description={
        <p className="text-sm text-muted mt-1">
          Upload a .mp3 file to attach a greeting to this number.
        </p>
      }
      footer={
        <div className="flex justify-center gap-4 pt-2">
          <button
            onClick={handleSubmit}
            disabled={!file}
            className="px-8 py-2.5 bg-primary text-white rounded-lg text-sm font-bold tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
          >
            UPLOAD
          </button>
          <button
            onClick={handleClose}
            className="px-8 py-2.5 border-2 border-primary text-primary rounded-lg text-sm font-bold tracking-widest hover:bg-light transition-colors min-w-[120px]"
          >
            CANCEL
          </button>
        </div>
      }
      bodyClass="!pl-0 !pr-2"
    >
      {/* Drop zone */}
      <div
        className={`border-[3px] border-dashed rounded-2xl p-10 sm:p-14 text-center transition-colors cursor-pointer mb-4 ${
          dragActive
            ? "border-primary bg-primary/5"
            : "border-gray-300 hover:border-primary/40"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".mp3,audio/mpeg"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Upload icon */}
        <div className="flex justify-center mb-4">
          <div className="text-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
        </div>

        {file ? (
          <>
            <p className="text-base font-semibold text-primary mb-1">
              {file.name}
            </p>
            <p className="text-sm text-muted">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </>
        ) : (
          <>
            <p className="text-base font-semibold text-primary mb-1">
              Click to upload
            </p>
            <p className="text-sm text-muted">or drag and drop</p>
          </>
        )}
      </div>

      {error && (
        <p className="text-xs text-danger mb-3 text-center">{error}</p>
      )}

      {/* Upload instructions info box */}
      <div className="bg-primary border border-primary/20 rounded-xl p-4 flex gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
            <InfoOutlinedIcon
              className="text-white"
              style={{ fontSize: 16 }}
            />
          </div>
        </div>
        <div className="text-xs text-white space-y-0.5">
          <p className="font-bold">Upload Instructions:</p>
          <p>Click to upload a file, or drag and drop it here.</p>
          <p>Accepted format: .mp3</p>
          <p>Maximum file size: 5MB</p>
          <p>
            Uploading a new file will replace the existing greeting if one
            exists.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default UploadGreetingModal;
